import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';

import { tokens } from '@design';
// ============================================================
// Types
// ============================================================

export interface Permission {
  id: string;
  label: string;
}

export interface PermissionsSelectorProps {
  availablePermissions: Permission[];
  grantedPermissions: string[];
  onGrantedPermissionsChange: (permissions: string[]) => void;
}

// ============================================================
// Icon Components
// ============================================================

const CheckIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// PermissionsSelector Component
// ============================================================

export default function PermissionsSelector({
  availablePermissions,
  grantedPermissions,
  onGrantedPermissionsChange,
}: PermissionsSelectorProps) {
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const isSmallScreen = width < 768;

  const [availableSearch, setAvailableSearch] = useState('');
  const [grantedSearch, setGrantedSearch] = useState('');

  // Theme colors
  const themeColors = {
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    listBackground: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    border: colors.border,
    inputBackground: colors.inputBackground,
    grantedItemBackground: isDark ? 'rgba(59, 130, 246, 0.2)' : '#EFF6FF',
    checkboxBackground: colors.inputBackground,
  };

  // Filter permissions by search
  const filteredAvailable = availablePermissions
    .filter(p => !grantedPermissions.includes(p.id))
    .filter(p => p.label.includes(availableSearch));

  const filteredGranted = availablePermissions
    .filter(p => grantedPermissions.includes(p.id))
    .filter(p => p.label.includes(grantedSearch));

  // Toggle single permission
  const togglePermission = (permissionId: string) => {
    if (grantedPermissions.includes(permissionId)) {
      onGrantedPermissionsChange(grantedPermissions.filter(p => p !== permissionId));
    } else {
      onGrantedPermissionsChange([...grantedPermissions, permissionId]);
    }
  };

  // Add all permissions
  const addAllPermissions = () => {
    onGrantedPermissionsChange(availablePermissions.map(p => p.id));
  };

  // Remove all permissions
  const removeAllPermissions = () => {
    onGrantedPermissionsChange([]);
  };

  return (
    <View>
      <Text style={[styles.permissionsTitle, { color: themeColors.textPrimary }]}>{t('permissions.usagePermissions')}</Text>

      <View style={[styles.permissionsContainer, isSmallScreen && styles.permissionsContainerSmall]}>
        {/* Available Permissions */}
        <View style={[styles.permissionsList, { backgroundColor: themeColors.listBackground, borderColor: themeColors.border }]}>
          <Text style={[styles.permissionsListTitle, { color: themeColors.textPrimary }]}>{t('permissions.availablePermissions')}</Text>
          <Text style={[styles.permissionsListHint, { color: themeColors.textMuted }]}>{t('permissions.selectToChooseHint')}</Text>

          <View style={styles.searchBox}>
            <TextInput
              style={[styles.searchInput, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
              placeholder={t('permissions.searchPermissions')}
              placeholderTextColor={themeColors.textMuted}
              textAlign="right"
              value={availableSearch}
              onChangeText={setAvailableSearch}
            />
            <Text style={[styles.searchLabel, { color: themeColors.textMuted }]}>{t('common.search')}</Text>
          </View>

          <ScrollView style={styles.permissionsScroll}>
            {filteredAvailable.map((permission) => (
              <TouchableOpacity
                key={permission.id}
                style={styles.permissionItem}
                onPress={() => togglePermission(permission.id)}
              >
                <View style={[styles.checkbox, { backgroundColor: themeColors.checkboxBackground, borderColor: themeColors.border }]} />
                <Text style={[styles.permissionLabel, { color: themeColors.textPrimary }]}>{permission.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity style={styles.addAllBtn} onPress={addAllPermissions}>
            <Text style={styles.addAllText}>{t('permissions.addAll')} &raquo;</Text>
          </TouchableOpacity>
        </View>

        {/* Granted Permissions */}
        <View style={[styles.permissionsList, { backgroundColor: themeColors.listBackground, borderColor: themeColors.border }]}>
          <Text style={[styles.permissionsListTitle, { color: themeColors.textPrimary }]}>{t('permissions.grantedPermissions')}</Text>
          <Text style={[styles.permissionsListHint, { color: themeColors.textMuted }]}>{t('permissions.selectToRemoveHint')}</Text>

          <View style={styles.searchBox}>
            <TextInput
              style={[styles.searchInput, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
              placeholder={t('permissions.searchPermissions')}
              placeholderTextColor={themeColors.textMuted}
              textAlign="right"
              value={grantedSearch}
              onChangeText={setGrantedSearch}
            />
            <Text style={[styles.searchLabel, { color: themeColors.textMuted }]}>{t('common.search')}</Text>
          </View>

          <ScrollView style={styles.permissionsScroll}>
            {filteredGranted.map((permission) => (
              <TouchableOpacity
                key={permission.id}
                style={[styles.permissionItem, { backgroundColor: themeColors.grantedItemBackground }]}
                onPress={() => togglePermission(permission.id)}
              >
                <View style={[styles.checkbox, styles.checkboxChecked]}>
                  <CheckIcon size={12} color={tokens.colors.raw.white} />
                </View>
                <Text style={[styles.permissionLabel, { color: themeColors.textPrimary }]}>{permission.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity style={styles.removeAllBtn} onPress={removeAllPermissions}>
            <Text style={styles.removeAllText}>&laquo; {t('permissions.removeAll')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  permissionsTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '600',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  permissionsContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
  },
  permissionsContainerSmall: {
    flexDirection: 'column',
  },
  permissionsList: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
  },
  permissionsListTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["1"],
  },
  permissionsListHint: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  searchInput: {
    flex: 1,
    height: 36,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-6"],
    paddingHorizontal: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: tokens.colors.semantic.general.foreground,
    backgroundColor: tokens.colors.raw.white,
    textAlign: getTextAlign(isRTL),
  },
  searchLabel: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: tokens.colors.raw.slate["500"],
  },
  permissionsScroll: {
    maxHeight: 150,
    marginBottom: tokens.spacing.absolute["3"],
  },
  permissionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    gap: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  permissionItemGranted: {
    backgroundColor: '#EFF6FF',
  },
  checkbox: {
    width: 18,
    height: 18,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-4"],
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
  },
  checkboxChecked: {
    backgroundColor: tokens.colors.raw.blue["500"],
    borderColor: tokens.colors.raw.blue["500"],
  },
  permissionLabel: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  addAllBtn: {
    alignSelf: 'flex-end',
    paddingVertical: tokens.spacing.absolute["1"],
  },
  addAllText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: tokens.colors.raw.blue["500"],
  },
  removeAllBtn: {
    alignSelf: 'flex-start',
    paddingVertical: tokens.spacing.absolute["1"],
  },
  removeAllText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: tokens.colors.raw.red["500"],
  },
});

const styles = getStyles(true);
